'use client'
import type { PropsWithChildren } from 'react'
import { MDSForm, type MDSFormProps } from './mds-form'
import { GreenhouseIframeForm } from './greenhouse-iframe-form'
import { StandaloneLink } from '@hashicorp/react-mds/src/components/standalone-link'
import s from './style.module.css'
import {
	useApplicationForm,
	ApplicationFormProvider,
	type ApplicationFormProviderProps,
} from 'lib/careers/context'
import { useRouter } from 'next/navigation'
import { logger } from 'lib/datadog/rum-logger'
import Recaptcha from 'components/recaptcha'
import guardedSubmission from 'components/recaptcha/guarded-submission'
import { postFormDataToProxy } from 'lib/careers/greenhouse-careers'

/**
 *
 * There are 2 application forms that more or less do the same thing.
 *
 * This component adds checks to decide which version of the form to render
 *
 * Why do we have 2 forms?
 *
 * {@link MDSForm} form matches the Hashicorp Design System and allows us granulairty to gather more insights into how interact with our components
 * We have no insight into what goes on in the greenhouse iframe form, rendered by {@link GreenhouseIframeForm}
 *
 * However, we aren't currently at parity with the Greenhouse iframe forms so we still need to fallback to the iframe for some features
 *
 * Specifically,
 * - we don't have a locations feature with the google maps api
 * - we don't have greenhouse's index of schools, major, and degree types for education questions (only asked to interns)
 *
 * This means that we fall back to the greenhouse-iframe for onsite / hybrid jobs
 * and internships
 */
function Form({ career, children }: PropsWithChildren<MDSFormProps>) {
	const { hideApplication, formState } = useApplicationForm()
	const isFormVisible = formState.isVisible

	if (!isFormVisible) {
		return children
	}

	return (
		<div className={s.application}>
			<StandaloneLink
				text="Back to job posting"
				color="secondary"
				onClick={hideApplication}
				icon="arrow-left"
			/>
			<h1 className={s.heading}>HashiCorp Employment Application</h1>
			<MDSForm career={career} />
			<Recaptcha formStatus={formState.submissionStatus} />
		</div>
	)
}

export function LinkedInTrackingPixels() {
	// Add two LinkedIn tracking pixels to the page when an application submission is complete,
	// so we can appropriately track the success of our Campaigns.
	return (
		<>
			{/* eslint-disable-next-line @next/next/no-img-element */}
			<img
				height="1"
				width="1"
				style={{ display: 'none' }}
				alt=""
				src="https://px.ads.linkedin.com/collect/?pid=537212&conversionId=550876&fmt=gif"
			/>
		</>
	)
}

const handleSubmitClick = async (
	formData: FormData,
	listingId: string
): Promise<void> => {
	const formDataLog = Object.fromEntries(formData.entries())
	await guardedSubmission('careers_submit_application', formDataLog, listingId)
	await postFormDataToProxy(formData, listingId)
}

function ApplicationFormWrapper({
	career,
	children,
	initialFormState,
	...rest
}: ApplicationFormProviderProps) {
	const router = useRouter()

	return (
		<ApplicationFormProvider
			career={career}
			initialFormState={initialFormState}
			onSubmitClick={handleSubmitClick}
			onSubmitSuccess={() => {
				router.push('/thank-you/job-listing')
			}}
			onSubmitError={(err) => {
				logger.error('careers_application_submit_error', err)
				window.scrollTo(0, 0)
			}}
			{...rest}
		>
			<Form career={career}>{children}</Form>
		</ApplicationFormProvider>
	)
}

export { ApplicationFormWrapper as ApplicationForm }
